import { Datepicker, Input, Switch, Textarea } from '@ibsys/kit';
import { FC } from 'react';
import { type KmAttrType, KM_ATTR_DATA_TYPES } from '@/entities/ontology';
import { isKmAttrJsonInput, isKmAttrRef, type KmAttrFormValue } from './attrFormValue';
import { KmAttrChoiceInput } from './KmAttrChoiceInput';
import { KmAttrRefInput } from './KmAttrRefInput';

type Props = {
  attrType: KmAttrType;
  value?: KmAttrFormValue;
  onChange?: (value: KmAttrFormValue) => void;
  disabled?: boolean;
  error?: string;
  placeholder?: string;
};

/**
 * Ввод одного значения по типу атрибута: текст, число, дата, дата со временем, да/нет,
 * выбор значения справочника или запись JSON.
 */
export const KmAttrScalarInput: FC<Props> = ({ attrType, value, onChange, disabled, error, placeholder }) => {
  const text = typeof value === 'string' ? value : '';
  const { dataType } = attrType;

  if (dataType === KM_ATTR_DATA_TYPES.BOOL) {
    return <Switch value={value === true} onChange={(next) => onChange?.(next)} disabled={disabled} error={error} />;
  }

  if (isKmAttrJsonInput(attrType)) {
    return (
      <Textarea
        value={text}
        onChange={(event) => onChange?.(event.target.value)}
        disabled={disabled}
        error={error}
        placeholder={placeholder}
        className="w-full"
      />
    );
  }

  if (dataType === KM_ATTR_DATA_TYPES.SELECT || dataType === KM_ATTR_DATA_TYPES.MULTISELECT) {
    if (isKmAttrRef(attrType)) {
      return (
        <KmAttrRefInput
          refEntityType={attrType.options?.refEntityType ?? ''}
          value={value}
          onChange={onChange}
          disabled={disabled}
          error={error}
          placeholder={placeholder}
        />
      );
    }

    return (
      <KmAttrChoiceInput
        choices={attrType.options?.choices ?? []}
        value={value}
        onChange={onChange}
        disabled={disabled}
        error={error}
        placeholder={placeholder}
      />
    );
  }

  if (dataType === KM_ATTR_DATA_TYPES.DATE) {
    return (
      <Datepicker
        outputType="iso-date"
        value={text}
        onChange={(next) => onChange?.(typeof next === 'string' ? next : '')}
        disabled={disabled}
        error={error}
        placeholder={placeholder}
        isClearable
        className="w-full"
      />
    );
  }

  if (dataType === KM_ATTR_DATA_TYPES.DATETIME) {
    return (
      <Datepicker
        showTimeInputs
        value={text}
        onChange={(next) => onChange?.(typeof next === 'string' ? next : '')}
        disabled={disabled}
        error={error}
        placeholder={placeholder}
        isClearable
        className="w-full"
      />
    );
  }

  if (dataType === KM_ATTR_DATA_TYPES.INT || dataType === KM_ATTR_DATA_TYPES.DECIMAL) {
    return (
      <Input
        type="number"
        value={text}
        onChange={(event) => onChange?.(event.target.value)}
        disabled={disabled}
        error={error}
        placeholder={placeholder}
        className="w-full"
      />
    );
  }

  // Текст читается целиком: высота поля следует объёму значения, начиная с одной строки
  return (
    <Textarea
      rows={1}
      value={text}
      onChange={(event) => onChange?.(event.target.value)}
      disabled={disabled}
      error={error}
      placeholder={placeholder}
      className="w-full"
    />
  );
};
